import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  Heart, IndianRupee, ChevronRight, Copy, Sparkles, ShieldCheck, Lock,
  Disc3, Video, BookOpen, Music,
} from "lucide-react";
import { FaSpotify, FaYoutube, FaInstagram, FaPaypal, FaCreditCard } from "react-icons/fa";
import SEO from "@/components/SEO";
import { PageHeader, Section, Reveal, Eyebrow } from "@/components/Section";
import { STREAMING } from "@/components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Newsletter from "@/components/Newsletter";
import { http } from "@/lib/api";
import { trackDonation } from "@/lib/analytics";
import { TID } from "@/constants/testIds";

const AMOUNTS = [
  { value: 101, blessing: "One śloka recorded" },
  { value: 251, blessing: "Studio hour" },
  { value: 501, blessing: "Master session" },
  { value: 1100, blessing: "Cinematic strings pass" },
  { value: 2100, blessing: "Producer credit consideration" },
];

const UPI_ID = "kashix@upi";
const UPI_NAME = "KASHiX";

const impactCards = [
  { Icon: Disc3, title: "Studio recordings", body: "Every ₹ becomes vocal takes, orchestration, mastering." },
  { Icon: Video, title: "Music videos", body: "Cinematic devotional visuals on YouTube." },
  { Icon: BookOpen, title: "Lyrics preservation", body: "A free, forever library — Sanskrit, Hindi, English." },
  { Icon: Music, title: "Future albums", body: "Concept records for Hanuman, Shiva, Devi." },
];

const streamingWays = [
  { tid: TID.supportSpotify, href: STREAMING.spotify, Icon: FaSpotify, title: "Stream on Spotify", desc: "Save every release." },
  { tid: TID.supportYoutube, href: STREAMING.youtube, Icon: FaYoutube, title: "Subscribe on YouTube", desc: "New visuals monthly." },
  { tid: TID.supportInstagram, href: STREAMING.instagram, Icon: FaInstagram, title: "Follow on Instagram", desc: "BTS from the studio." },
];

const loadScript = (src) => new Promise((resolve, reject) => {
  if (document.querySelector(`script[src='${src}']`)) return resolve(true);
  const s = document.createElement("script");
  s.src = src; s.async = true;
  s.onload = () => resolve(true);
  s.onerror = () => reject(new Error("script load failed"));
  document.head.appendChild(s);
});

const Support = () => {
  const [amount, setAmount] = useState(501);
  const [custom, setCustom] = useState("");
  const [step, setStep] = useState("choose");
  const [method, setMethod] = useState("upi");
  const [config, setConfig] = useState({ razorpay_enabled: false, paypal_enabled: false });
  const [wall, setWall] = useState([]);
  const [supporterName, setSupporterName] = useState("");
  const [supporterMessage, setSupporterMessage] = useState("");

  useEffect(() => {
    http.get("/config/payments").then((r) => setConfig(r.data)).catch(() => {});
    http.get("/donations/wall").then((r) => setWall(r.data)).catch(() => {});
  }, [step]);

  const finalAmount = useMemo(() => (custom ? Number(custom) : amount), [custom, amount]);

  const upiHref = useMemo(() => {
    const params = new URLSearchParams({
      pa: UPI_ID, pn: UPI_NAME, am: String(finalAmount || 0), cu: "INR",
      tn: `Support KASHiX ₹${finalAmount}`,
    });
    return `upi://pay?${params.toString()}`;
  }, [finalAmount]);

  const copyUpi = async () => {
    try { await navigator.clipboard.writeText(UPI_ID); toast.success("UPI ID copied"); }
    catch { toast.error("Copy failed"); }
  };

  const proceed = () => {
    if (!finalAmount || finalAmount < 10) { toast.error("Please choose an amount"); return; }
    setStep("pay");
  };

  const recordManual = async (payMethod) => {
    try {
      await http.post("/donations", {
        amount: finalAmount, currency: "INR", method: payMethod,
        supporter_name: supporterName, supporter_message: supporterMessage,
        show_on_wall: true,
      });
      trackDonation(finalAmount, payMethod);
      setStep("thanks");
    } catch (e) { toast.error("Could not record donation"); }
  };

  const payRazorpay = async () => {
    if (!config.razorpay_enabled) return toast.info("Razorpay will be enabled soon.");
    try {
      const { data } = await http.post("/donations/razorpay/order", {
        amount: finalAmount, currency: "INR", method: "razorpay",
        supporter_name: supporterName, supporter_message: supporterMessage, show_on_wall: true,
      });
      await loadScript("https://checkout.razorpay.com/v1/checkout.js");
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.order.amount,
        currency: data.order.currency,
        order_id: data.order.id,
        name: "KASHiX",
        description: "Support the independent devotional music mission",
        image: "https://is1-ssl.mzstatic.com/image/thumb/Music211/v4/24/62/34/24623467-b28c-f8c1-fee0-e023b59a90ce/artwork.jpg/300x300bb.jpg",
        theme: { color: "#FF9933" },
        prefill: { name: supporterName || "" },
        handler: async (resp) => {
          try {
            await http.post("/donations/razorpay/verify", {
              donation_id: data.donation_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            trackDonation(finalAmount, "razorpay");
            setStep("thanks");
          } catch (e) { toast.error("Verification failed"); }
        },
      });
      rzp.open();
    } catch (e) { toast.error(e?.response?.data?.detail || "Razorpay error"); }
  };

  const payPayPal = async () => {
    if (!config.paypal_enabled) return toast.info("PayPal will be enabled soon.");
    try {
      const { data } = await http.post("/donations/paypal/order", {
        amount: Math.max(1, Math.round(finalAmount / 80)), // rough INR→USD conversion (client-side visual only)
        currency: "USD", method: "paypal",
        supporter_name: supporterName, supporter_message: supporterMessage, show_on_wall: true,
      });
      // Redirect user to PayPal approval flow (SDK-less minimal path).
      const approve = `https://www.${config.paypal_env === "live" ? "" : "sandbox."}paypal.com/checkoutnow?token=${data.order_id}`;
      window.open(approve, "_blank", "noopener");
      toast.info("Complete payment in the PayPal window, then click 'I have paid'.");
    } catch (e) { toast.error(e?.response?.data?.detail || "PayPal error"); }
  };

  return (
    <>
      <SEO title="Support KASHiX" description="Help preserve and grow original devotional music. UPI, QR, Razorpay & PayPal." />
      <PageHeader
        eyebrow="Support KASHiX"
        hindi="कलाकार का सहयोग"
        title="Support KASHiX"
        subtitle="Help preserve and grow original devotional music. Every contribution funds studio time, orchestration, videography and the next release."
      />

      {/* Impact */}
      <Section className="!py-8">
        <Reveal>
          <div className="glass rounded-3xl p-8 grid gap-6 md:grid-cols-4">
            {impactCards.map(({ Icon, title, body }) => (
              <div key={title}>
                <div className="w-9 h-9 rounded-full bg-[#FF9933]/10 border border-[#FF9933]/40 flex items-center justify-center text-[#FF9933]"><Icon size={14} /></div>
                <div className="mt-3 text-white text-sm">{title}</div>
                <div className="text-white/60 text-xs mt-1">{body}</div>
              </div>
            ))}
          </div>
        </Reveal>
      </Section>

      {/* Donation flow */}
      <Section className="!py-8">
        <div className="grid gap-6 lg:grid-cols-12 items-start">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="glass-strong rounded-3xl p-8 md:p-10 border border-[#FF9933]/20 relative overflow-hidden">
                <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ background: "radial-gradient(60% 60% at 50% 0%, rgba(255,153,51,0.25), transparent 70%)" }} />
                <div className="relative">
                  <AnimatePresence mode="wait">
                    {step === "choose" && (
                      <motion.div key="choose" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
                        <Eyebrow>Choose your offering</Eyebrow>
                        <div className="mt-4 font-hindi text-2xl text-[#FF9933]">दान चुनें</div>
                        <h3 className="mt-1 font-display text-3xl md:text-4xl text-white tracking-tight">Every ₹ funds ancient sound.</h3>
                        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-3">
                          {AMOUNTS.map((a) => (
                            <button key={a.value} onClick={() => { setAmount(a.value); setCustom(""); }} data-testid={`donate-${a.value}`}
                                    className={`group text-left rounded-2xl border p-4 transition ${amount === a.value && !custom ? "border-[#FF9933] bg-[#FF9933]/10" : "border-white/10 hover:border-[#FF9933]/60"}`}>
                              <div className="flex items-center gap-1 text-white font-display text-2xl"><IndianRupee size={16} className="opacity-70" />{a.value}</div>
                              <div className="mt-1 text-[10px] uppercase tracking-[0.25em] text-white/50">{a.blessing}</div>
                            </button>
                          ))}
                          <div className={`rounded-2xl border p-4 transition ${custom ? "border-[#FF9933] bg-[#FF9933]/10" : "border-white/10"}`}>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-1">Custom</div>
                            <div className="flex items-center gap-1"><IndianRupee size={14} className="text-white/60" />
                              <input data-testid="donate-custom" inputMode="numeric" value={custom} onChange={(e) => setCustom(e.target.value.replace(/[^0-9]/g, ""))} placeholder="0" className="w-full bg-transparent text-white font-display text-2xl focus:outline-none" />
                            </div>
                          </div>
                        </div>

                        <div className="mt-6 grid md:grid-cols-2 gap-3">
                          <input value={supporterName} onChange={(e) => setSupporterName(e.target.value)} placeholder="Your name (optional)"
                                 className="w-full bg-black/60 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF9933]" />
                          <input value={supporterMessage} onChange={(e) => setSupporterMessage(e.target.value)} placeholder="Blessing / message (optional)"
                                 className="w-full bg-black/60 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF9933]" />
                        </div>

                        <button onClick={proceed} data-testid="donate-proceed" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#FF9933] text-black px-6 py-3 text-xs uppercase tracking-[0.3em] font-semibold hover:bg-[#FFD700] hover:shadow-[0_0_30px_rgba(255,153,51,0.55)] transition-all">
                          Continue with ₹{finalAmount || 0} <ChevronRight size={14} />
                        </button>
                      </motion.div>
                    )}

                    {step === "pay" && (
                      <motion.div key="pay" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
                        <Eyebrow>Choose payment method</Eyebrow>
                        <h3 className="mt-4 font-display text-3xl md:text-4xl text-white tracking-tight">Send ₹{finalAmount} to KASHiX</h3>

                        <div className="mt-6 flex flex-wrap gap-2">
                          {[
                            { id: "upi", label: "UPI Apps" },
                            { id: "qr", label: "QR Code" },
                            { id: "razorpay", label: `Card / Net Banking${config.razorpay_enabled ? "" : " · soon"}` },
                            { id: "paypal", label: `PayPal (Intl.)${config.paypal_enabled ? "" : " · soon"}` },
                          ].map((m) => (
                            <button key={m.id} onClick={() => setMethod(m.id)} data-testid={`method-${m.id}`}
                                    className={`px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.25em] border transition ${method === m.id ? "bg-[#FF9933] text-black border-[#FF9933]" : "border-white/10 text-white/70 hover:border-[#FF9933] hover:text-[#FF9933]"}`}>
                              {m.label}
                            </button>
                          ))}
                        </div>

                        <div className="mt-8">
                          {method === "upi" && (
                            <div className="space-y-4">
                              <a href={upiHref} onClick={() => recordManual("upi")} data-testid="donate-upi"
                                 className="inline-flex items-center gap-2 rounded-full bg-[#FF9933] text-black px-6 py-3 text-xs uppercase tracking-[0.3em] font-semibold hover:bg-[#FFD700] transition">
                                Open UPI App · ₹{finalAmount}
                              </a>
                              <div className="text-[11px] text-white/50">On mobile this opens Google Pay, PhonePe, Paytm, BHIM or any UPI app you have installed.</div>
                              <button onClick={copyUpi} className="inline-flex items-center gap-2 rounded-full border border-white/15 text-white px-5 py-2.5 text-xs uppercase tracking-[0.3em] hover:text-[#FF9933] hover:border-[#FF9933] transition">
                                <Copy size={12} /> Copy UPI ID · {UPI_ID}
                              </button>
                            </div>
                          )}
                          {method === "qr" && (
                            <div className="flex items-center gap-6 flex-wrap">
                              <div className="p-4 rounded-2xl bg-white" data-testid="donate-qr">
                                <QRCodeSVG value={upiHref} size={200} bgColor="#ffffff" fgColor="#0A0A0A" includeMargin={false} level="M" />
                              </div>
                              <div className="max-w-xs">
                                <div className="text-white text-sm">Scan with any UPI app.</div>
                                <div className="mt-2 text-white/50 text-xs">Google Pay · PhonePe · Paytm · BHIM · Amazon Pay.</div>
                                <div className="mt-4 text-[10px] uppercase tracking-[0.3em] text-[#FF9933]">Amount</div>
                                <div className="mt-1 font-display text-3xl text-white">₹{finalAmount}</div>
                                <button onClick={copyUpi} className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/15 text-white px-4 py-2 text-[10px] uppercase tracking-[0.3em] hover:text-[#FF9933] hover:border-[#FF9933]">
                                  <Copy size={10} /> Copy UPI ID
                                </button>
                              </div>
                            </div>
                          )}
                          {method === "razorpay" && (
                            <div className="space-y-3">
                              <button
                                onClick={payRazorpay}
                                data-testid="donate-razorpay"
                                disabled={!config.razorpay_enabled}
                                className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-xs uppercase tracking-[0.3em] font-semibold transition ${config.razorpay_enabled ? "bg-[#FF9933] text-black hover:bg-[#FFD700]" : "bg-white/5 text-white/40 border border-white/10 cursor-not-allowed"}`}
                              >
                                <FaCreditCard size={14} /> Pay ₹{finalAmount} · Razorpay
                              </button>
                              <div className="text-[11px] text-white/50">
                                {config.razorpay_enabled
                                  ? "Cards · Net Banking · Wallets · UPI, all in one secure Razorpay checkout."
                                  : "Razorpay checkout launches once RAZORPAY_KEY_ID and RAZORPAY_KEY_SECRET env vars are set. Use UPI or QR meanwhile."}
                              </div>
                            </div>
                          )}
                          {method === "paypal" && (
                            <div className="space-y-3">
                              <button
                                onClick={payPayPal}
                                data-testid="donate-paypal"
                                disabled={!config.paypal_enabled}
                                className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-xs uppercase tracking-[0.3em] font-semibold transition ${config.paypal_enabled ? "bg-[#FF9933] text-black hover:bg-[#FFD700]" : "bg-white/5 text-white/40 border border-white/10 cursor-not-allowed"}`}
                              >
                                <FaPaypal size={16} /> Support with PayPal
                              </button>
                              <div className="text-[11px] text-white/50">
                                {config.paypal_enabled
                                  ? `PayPal ${config.paypal_env === "live" ? "live" : "sandbox"} checkout. For international supporters.`
                                  : "PayPal launches once PAYPAL_CLIENT_ID and PAYPAL_CLIENT_SECRET env vars are set."}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="mt-8 flex items-center gap-3 flex-wrap text-[11px] text-white/50">
                          <button onClick={() => setStep("choose")} className="hover:text-white">← Change amount</button>
                          <span className="text-white/20">·</span>
                          <button onClick={() => recordManual(method)} data-testid="donate-i-paid" className="hover:text-white">I have paid →</button>
                        </div>
                      </motion.div>
                    )}

                    {step === "thanks" && (
                      <motion.div key="thanks" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} className="text-center py-6">
                        <Sparkles size={32} className="mx-auto text-[#FF9933] pulse-glow" />
                        <div className="mt-6 font-hindi text-2xl text-[#FF9933]">धन्यवाद · Blessing received</div>
                        <h3 className="mt-1 font-display text-4xl md:text-5xl text-white tracking-tight">Thank you.</h3>
                        <p className="mt-4 text-white/70 max-w-lg mx-auto italic font-editorial text-lg">
                          &ldquo;May the mantra return to you multiplied. May your work find focus. May your day be quieter.&rdquo;
                        </p>
                        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
                          <button onClick={() => { setStep("choose"); setCustom(""); setAmount(501); setSupporterName(""); setSupporterMessage(""); }} className="inline-flex items-center gap-2 rounded-full border border-white/15 text-white px-5 py-3 text-xs uppercase tracking-[0.3em] hover:text-[#FF9933] hover:border-[#FF9933]">Give again</button>
                          <Link to="/releases" className="inline-flex items-center gap-2 rounded-full bg-[#FF9933] text-black px-5 py-3 text-xs uppercase tracking-[0.3em] font-semibold hover:bg-[#FFD700] transition">Return to Music</Link>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </Reveal>
            <Reveal>
              <div className="mt-4 flex items-center justify-center gap-6 flex-wrap text-[11px] text-white/50">
                <span className="inline-flex items-center gap-2"><Lock size={13} className="text-[#FF9933]" /> Secured via your UPI app / Razorpay / PayPal</span>
                <span className="inline-flex items-center gap-2"><ShieldCheck size={13} className="text-[#FF9933]" /> No card details stored by KASHiX</span>
                <span className="inline-flex items-center gap-2">100% goes to the artist</span>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5 space-y-6">
            <Reveal delay={0.1}>
              <div className="glass rounded-3xl p-8">
                <div className="text-[10px] uppercase tracking-[0.35em] text-[#FF9933]">Free ways to support</div>
                <div className="mt-4 space-y-3">
                  {streamingWays.map(({ tid, href, Icon, title, desc }) => (
                    <a key={title} data-testid={tid} href={href} target="_blank" rel="noreferrer noopener" className="group flex items-center gap-4 rounded-2xl border border-white/5 hover:border-[#FF9933]/40 p-4 transition">
                      <div className="w-11 h-11 rounded-full bg-[#FF9933]/10 border border-[#FF9933]/40 flex items-center justify-center text-[#FF9933] group-hover:bg-[#FF9933] group-hover:text-black transition-colors"><Icon size={16} /></div>
                      <div className="min-w-0"><div className="text-white text-sm">{title}</div><div className="text-white/50 text-xs truncate">{desc}</div></div>
                    </a>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="glass rounded-3xl p-8" data-testid="supporters-wall">
                <div className="text-[10px] uppercase tracking-[0.35em] text-[#FF9933]">Supporters Wall</div>
                <div className="mt-2 text-white/60 text-xs">Anonymised · most recent first.</div>
                <div className="mt-4 space-y-3 max-h-[340px] overflow-y-auto no-scrollbar">
                  {wall.length === 0 && <div className="text-white/50 text-sm">Be the first name on this wall.</div>}
                  {wall.map((s) => (
                    <div key={s.id} className="flex items-start gap-3 rounded-xl border border-white/5 p-3">
                      <div className="w-9 h-9 rounded-full bg-[#FF9933]/10 border border-[#FF9933]/40 flex items-center justify-center text-[#FF9933] text-xs font-semibold">
                        {(s.supporter_name || "A")[0].toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-white text-sm truncate">{s.supporter_name}</div>
                          <div className="text-[#FF9933] text-sm font-semibold">₹{s.amount}</div>
                        </div>
                        {s.supporter_message && <div className="text-white/60 text-xs italic mt-0.5 line-clamp-2">&ldquo;{s.supporter_message}&rdquo;</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.18}>
              <div className="glass rounded-3xl p-8" data-testid="support-faq">
                <div className="text-[10px] uppercase tracking-[0.35em] text-[#FF9933]">Frequently asked</div>
                <Accordion type="single" collapsible className="mt-4">
                  <AccordionItem value="a" className="border-white/5"><AccordionTrigger className="text-left text-white text-sm hover:text-[#FF9933]">Is my payment secure?</AccordionTrigger><AccordionContent className="text-white/60 text-sm">Yes. UPI happens inside your bank app. Razorpay is PCI-DSS Level 1. PayPal handles buyer protection. KASHiX never sees your card or bank credentials.</AccordionContent></AccordionItem>
                  <AccordionItem value="b" className="border-white/5"><AccordionTrigger className="text-left text-white text-sm hover:text-[#FF9933]">Do I receive a receipt?</AccordionTrigger><AccordionContent className="text-white/60 text-sm">Your UPI / Razorpay / PayPal generates the official transaction receipt. For a personal thank-you note, drop a message on Contact.</AccordionContent></AccordionItem>
                  <AccordionItem value="c" className="border-white/5"><AccordionTrigger className="text-left text-white text-sm hover:text-[#FF9933]">Can I contribute internationally?</AccordionTrigger><AccordionContent className="text-white/60 text-sm">Yes — PayPal is available for international supporters (when enabled). UPI is India-only.</AccordionContent></AccordionItem>
                  <AccordionItem value="d" className="border-white/5"><AccordionTrigger className="text-left text-white text-sm hover:text-[#FF9933]">Where does the money go?</AccordionTrigger><AccordionContent className="text-white/60 text-sm">100% funds independent production: studio time, session musicians, mastering, music videos, and the free lyrics library.</AccordionContent></AccordionItem>
                </Accordion>
              </div>
            </Reveal>
          </div>
        </div>
      </Section>

      <Section className="!py-16">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl glass-strong p-10 text-center">
            <Heart className="mx-auto text-[#FF9933]" />
            <div className="mt-4 font-hindi text-2xl text-[#FF9933]">भीतरी संकुल</div>
            <h3 className="mt-1 font-display text-3xl md:text-4xl text-white tracking-tight">Stay in the temple.</h3>
            <p className="mt-4 text-white/70 max-w-lg mx-auto">First access to releases, licensing packs and behind-the-scenes.</p>
            <div className="mt-6 max-w-md mx-auto"><Newsletter /></div>
          </div>
        </Reveal>
      </Section>
    </>
  );
};

export default Support;
